import { useEffect, useRef, useState } from 'preact/hooks';
import { itemDef, ITEMS } from '../../game/items';
import { registry } from '../../sim/registry';
import { api } from '../../ui/app/api';
import { closePanel, ui } from '../../ui/app/store';
import { Window } from '../../ui/app/Window';
import type { NetSession } from '../NetSession';
import { type ChatChannel, type ChatLine, netUi } from '../netStore';
import { PARTY_MAX } from '../party';
import type { TradeLine } from '../trade';
import './net.css';

let session: NetSession | null = null;

export function setNetSession(s: NetSession): void {
  session = s;
}

const net = (): NetSession | null => session;
const className = (id: string): string => registry.classes.get(id)?.name ?? id;
const fmtTime = (ms: number): string => {
  const s = Math.max(0, Math.floor(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

/** Re-renders every `ms` (countdowns). */
function useTicker(ms: number): number {
  const [t, setT] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setT(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return t;
}

// ===================================================================== chat

const TABS: { id: 'all' | ChatChannel; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'zone', label: 'Area' },
  { id: 'party', label: 'Party' },
  { id: 'guild', label: 'Guild' },
  { id: 'whisper', label: 'Whisper' },
];

const CH_LABEL: Record<ChatChannel, string> = { zone: 'Area', party: 'Party', guild: 'Guild', whisper: 'Whisper', system: 'System' };

function ChatLineView({ l }: { l: ChatLine }) {
  return (
    <div class={`chat-line ch-${l.ch}`}>
      {l.ch !== 'system' && <span class="chat-ch">[{CH_LABEL[l.ch]}]</span>}
      {l.from && <span class="chat-from">{l.from}:</span>}
      <span class="chat-text">{l.text}</span>
    </div>
  );
}

export function ChatBox() {
  const [tab, setTab] = useState<'all' | ChatChannel>('all');
  const [send, setSend] = useState<'zone' | 'party' | 'guild'>('zone');
  const [text, setText] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const log = useRef<HTMLDivElement>(null);
  const open = ui.chatOpen.value;
  const lines = netUi.chat.value;
  const now = useTicker(2000);
  const shown = (tab === 'all' ? lines : lines.filter((l) => l.ch === tab || l.ch === 'system')).slice(-60);
  const faded = !open && (lines.length === 0 || now - (lines[lines.length - 1]?.at ?? 0) > 15000);

  useEffect(() => {
    if (open) {
      input.current?.focus();
      netUi.chatUnread.value = 0;
    }
  }, [open]);
  useEffect(() => {
    if (log.current) log.current.scrollTop = log.current.scrollHeight;
  }, [lines.length, tab, open]);

  const submit = () => {
    const t = text.trim();
    if (t) api().sendChat(t, send);
    setText('');
    ui.chatOpen.value = false;
    input.current?.blur();
  };

  return (
    <div class={`chatbox interactive${open ? ' open' : ''}${faded ? ' faded' : ''}${lines.length === 0 ? ' empty' : ''}`} onPointerDown={(e) => e.stopPropagation()}>
      <div class="chat-tabs">
        {TABS.map((t) => (
          <button key={t.id} class={`chat-tab${tab === t.id ? ' on' : ''}`} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
        <span class="chat-status" title={netUi.status.value === 'online' ? `Latency ${netUi.rtt.value} ms` : 'Offline'}>
          <i class={`dot ${netUi.status.value}`} />
          {netUi.zonePeers.value.length > 0 ? `${netUi.zonePeers.value.length + 1} here` : ''}
        </span>
      </div>
      <div class="chat-log" ref={log}>
        {shown.map((l) => (
          <ChatLineView key={l.id} l={l} />
        ))}
      </div>
      {open ? (
        <div class="chat-input">
          <select value={send} onChange={(e) => setSend((e.target as HTMLSelectElement).value as typeof send)}>
            <option value="zone">Area</option>
            <option value="party">Party</option>
            <option value="guild">Guild</option>
          </select>
          <input
            ref={input}
            maxLength={200}
            value={text}
            placeholder="Say something… (/w name to whisper)"
            onInput={(e) => setText((e.target as HTMLInputElement).value)}
            onKeyDown={(e) => {
              e.stopPropagation();
              if (e.key === 'Enter') submit();
              if (e.key === 'Escape') {
                setText('');
                ui.chatOpen.value = false;
                (e.target as HTMLInputElement).blur();
              }
            }}
            onBlur={() => {
              if (!text) ui.chatOpen.value = false;
            }}
          />
        </div>
      ) : (
        <div class="chat-hint">
          Press <kbd>Enter</kbd> to chat{netUi.chatUnread.value > 0 ? ` · ${netUi.chatUnread.value} new` : ''}
        </div>
      )}
    </div>
  );
}

// ===================================================================== invites / ready check / queue

export function Invites() {
  const now = useTicker(500);
  const list = netUi.invites.value.filter((i) => i.expires > now);
  if (list.length === 0) return null;
  return (
    <div class="net-invites interactive" onPointerDown={(e) => e.stopPropagation()}>
      {list.map((i) => (
        <div key={i.id} class={`net-invite kind-${i.kind}`}>
          <div class="ni-text">
            <b>{i.fromName}</b> {i.detail}
          </div>
          <div class="ni-bar">
            <i style={{ width: `${Math.max(0, Math.min(100, ((i.expires - now) / 30000) * 100))}%` }} />
          </div>
          <div class="ni-btns">
            <button class="btn btn--gold" onClick={() => net()?.answerInvite(i.id, true)}>
              Accept
            </button>
            <button class="btn" onClick={() => net()?.answerInvite(i.id, false)}>
              Decline
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function ReadyCheck() {
  const now = useTicker(250);
  const r = netUi.readyCheck.value;
  if (!r) return null;
  const left = r.expires - now;
  return (
    <div class="ready-check panel interactive" onPointerDown={(e) => e.stopPropagation()}>
      <div class="panel__title">Ready Check</div>
      <div class="rc-body">
        <p>
          <b>{r.leaderName}</b> wants to enter <b>{r.label}</b>.
        </p>
        <ul class="rc-list">
          {r.responses.map((x, i) => (
            <li key={i} class={x.ok === true ? 'ok' : x.ok === false ? 'no' : 'wait'}>
              <span>{x.name}</span>
              <span>{x.ok === true ? 'Ready' : x.ok === false ? 'Declined' : '…'}</span>
            </li>
          ))}
        </ul>
        <div class="ni-bar">
          <i style={{ width: `${Math.max(0, Math.min(100, (left / 20000) * 100))}%` }} />
        </div>
        <div class="ni-btns">
          {r.isLeader ? (
            <button class="btn" onClick={() => net()?.party.cancelReadyCheck()}>
              Cancel
            </button>
          ) : r.mine === null ? (
            <>
              <button class="btn btn--gold" onClick={() => net()?.party.answerReadyCheck(true)}>
                Ready
              </button>
              <button class="btn" onClick={() => net()?.party.answerReadyCheck(false)}>
                Decline
              </button>
            </>
          ) : (
            <span class="muted">Waiting for the others… {fmtTime(left)}</span>
          )}
        </div>
      </div>
    </div>
  );
}

export function QueueWidget() {
  const now = useTicker(1000);
  const q = netUi.queue.value;
  if (!q) return null;
  return (
    <div class="queue-pill interactive" onPointerDown={(e) => e.stopPropagation()}>
      <span class="qp-spin" />
      <span>
        Matchmaking · <b>{q.label}</b> · {q.count}/{q.size} · {fmtTime(now - q.since)}
      </span>
      <button class="panel__close" title="Leave queue" onClick={() => net()?.queue.cancel()}>
        ✕
      </button>
    </div>
  );
}

// ===================================================================== party / social panel

type SocialTab = 'party' | 'friends' | 'nearby' | 'guild' | 'online';

function PartyTab() {
  const members = netUi.party.value;
  const s = net();
  if (!s) return null;
  const me = members.find((m) => m.self);
  const leader = !!me?.leader;
  if (members.length === 0)
    return (
      <div class="social-empty">
        <p>You are not in a party.</p>
        <p class="muted">Invite players from the Nearby tab or type /invite name in chat. Parties of up to {PARTY_MAX} enter dungeons, raids and the Crucible together; the leader hosts.</p>
      </div>
    );
  return (
    <div>
      <table class="social-table">
        <tbody>
          {members.map((m) => (
            <tr key={m.uid} class={m.peer ? '' : 'offline'}>
              <td>{m.leader ? <span class="crown" title="Leader">♛</span> : null}</td>
              <td>
                <b>{m.name}</b>
                {m.self ? <span class="muted"> (you)</span> : null}
              </td>
              <td>{className(m.classId)}</td>
              <td>Lv {m.level}</td>
              <td>iLvl {m.ilvl}</td>
              <td class="muted">{m.peer ? (m.zone ?? '—') : 'Offline'}</td>
              <td class="row-btns">
                {leader && !m.self ? (
                  <>
                    <button class="btn" onClick={() => s.party.promote(m.uid)}>
                      Promote
                    </button>
                    <button class="btn" onClick={() => s.party.kick(m.uid)}>
                      Kick
                    </button>
                  </>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div class="social-foot">
        <span class="muted">{leader ? 'Enter a dungeon or raid to start a ready check.' : 'Your leader starts group content.'}</span>
        <button class="btn" onClick={() => s.party.leave()}>
          Leave party
        </button>
      </div>
    </div>
  );
}

function NearbyTab() {
  const s = net();
  const peers = netUi.zonePeers.value;
  const inGuild = !!netUi.guild.value;
  if (!s) return null;
  if (peers.length === 0) return <p class="social-empty muted">No other adventurers in this area right now.</p>;
  return (
    <table class="social-table">
      <tbody>
        {peers.map((p) => (
          <tr key={p.peer}>
            <td>
              <b>{p.name}</b>
              {p.guild ? <span class="gtag"> &lt;{p.guild}&gt;</span> : null}
            </td>
            <td>{className(p.classId)}</td>
            <td>Lv {p.level}</td>
            <td>iLvl {p.ilvl}</td>
            <td class="row-btns">
              <button class="btn" onClick={() => void s.invite(p.peer)}>
                Invite
              </button>
              <button class="btn" onClick={() => s.trade.request(p.peer, p.name)}>
                Trade
              </button>
              {!netUi.friends.value.some((f) => f.name === p.name) ? (
                <button class="btn" onClick={() => s.friends.request(p.peer, p.name)}>
                  Add friend
                </button>
              ) : null}
              <button
                class="btn"
                onClick={() => {
                  ui.chatOpen.value = true;
                }}
                title="Whisper: type /w name message"
              >
                Whisper
              </button>
              {inGuild ? (
                <button class="btn" onClick={() => s.inviteToGuild(p.peer)}>
                  Guild
                </button>
              ) : null}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function FriendsTab() {
  const s = net();
  const friends = netUi.friends.value;
  if (!s) return null;
  if (friends.length === 0)
    return (
      <div class="social-empty">
        <p>No friends yet.</p>
        <p class="muted">Add players you meet from the Nearby tab (or /friend name). You'll see when they're online and where, and can whisper or invite them from anywhere.</p>
      </div>
    );
  return (
    <table class="social-table">
      <tbody>
        {friends.map((f) => (
          <tr key={f.uid} class={f.online ? '' : 'offline'}>
            <td>
              <i class={`dot ${f.online ? 'online' : ''}`} />
            </td>
            <td>
              <b>{f.name}</b>
            </td>
            <td>{className(f.classId)}</td>
            <td>{f.online ? `Lv ${f.level}` : ''}</td>
            <td class="muted">{f.online ? (f.zone ?? '—') : 'Offline'}</td>
            <td class="row-btns">
              {f.online ? (
                <>
                  <button
                    class="btn"
                    onClick={() => {
                      ui.chatOpen.value = true;
                    }}
                    title={`Type /w ${f.name} message`}
                  >
                    Whisper
                  </button>
                  <button
                    class="btn"
                    onClick={() => {
                      const peer = s.friends.peerOf(f.uid);
                      if (peer) void s.invite(peer);
                    }}
                  >
                    Invite
                  </button>
                </>
              ) : null}
              <button
                class="btn"
                onClick={() => {
                  ui.confirm.value = { text: `Remove ${f.name} from your friends?`, okLabel: 'Remove', ok: () => s.friends.remove(f.uid) };
                }}
              >
                Remove
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function GuildTab() {
  const s = net();
  const g = netUi.guild.value;
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [err, setErr] = useState<string | null>(null);
  if (!s) return null;
  if (!g)
    return (
      <div class="social-empty">
        <p>You are not in a guild. Found one, or accept an invitation from a member.</p>
        <div class="guild-form">
          <input class="name-input" placeholder="Guild name" maxLength={20} value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} onKeyDown={(e) => e.stopPropagation()} />
          <input class="name-input tag" placeholder="TAG" maxLength={4} value={tag} onInput={(e) => setTag((e.target as HTMLInputElement).value.toUpperCase())} onKeyDown={(e) => e.stopPropagation()} />
          <button class="btn btn--gold" onClick={() => void s.guild.create(name, tag).then(setErr)}>
            Found guild
          </button>
        </div>
        {err ? <p class="err">{err}</p> : null}
      </div>
    );
  const leader = g.rank === 'leader';
  return (
    <div>
      <h3 class="guild-title">
        &lt;{g.tag}&gt; {g.name} <span class="muted">· {g.rank === 'leader' ? 'Guild Master' : 'Member'}</span>
      </h3>
      <table class="social-table">
        <tbody>
          {g.members.map((m) => (
            <tr key={m.uid} class={m.online ? '' : 'offline'}>
              <td>{m.rank === 'leader' ? <span class="crown">♛</span> : null}</td>
              <td>
                <b>{m.name}</b>
              </td>
              <td>{className(m.classId)}</td>
              <td>Lv {m.level}</td>
              <td class="muted">{m.online ? (m.zone ?? '—') : 'Offline'}</td>
              <td class="row-btns">
                {leader && m.rank !== 'leader' ? (
                  <button class="btn" onClick={() => s.guild.kick(m.uid)}>
                    Remove
                  </button>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div class="social-foot">
        <span class="muted">Guild chat: /g message. Invite nearby players from the Nearby tab.</span>
        <button
          class="btn"
          onClick={() => {
            ui.confirm.value = { text: `Leave ${g.name}?`, okLabel: 'Leave', ok: () => s.guild.leave() };
          }}
        >
          Leave guild
        </button>
      </div>
    </div>
  );
}

function OnlineTab() {
  const s = net();
  const status = netUi.status.value;
  const online = api().account.settings.online;
  return (
    <div class="kv-list">
      <div class="kv">
        <span>Status</span>
        <b class={`st-${status}`}>{status === 'online' ? 'Online' : status === 'connecting' ? 'Connecting…' : 'Offline'}</b>
      </div>
      <div class="kv">
        <span>Connection</span>
        <b>{netUi.mode.value === 'public' ? 'Peer-to-peer (public signalling)' : netUi.mode.value === 'relay' ? 'Local relay (development)' : netUi.mode.value === 'broadcast' ? 'Same browser (tabs)' : 'None'}</b>
      </div>
      <div class="kv">
        <span>Players here</span>
        <b>{netUi.zonePeers.value.length + 1}</b>
      </div>
      <div class="kv">
        <span>Latency</span>
        <b>{netUi.rtt.value ? `${netUi.rtt.value} ms` : '—'}</b>
      </div>
      <div class="kv">
        <span>Zone host</span>
        <b>{netUi.isHost.value ? 'You' : 'Another player'}</b>
      </div>
      <div class="kv">
        <span>Channel</span>
        <b>
          {[1, 2, 3].map((n) => (
            <button key={n} class={`btn ch-btn${netUi.channel.value === n ? ' btn--gold' : ''}`} disabled={!s || status !== 'online'} onClick={() => s?.switchChannel(n)}>
              {n}
            </button>
          ))}
        </b>
      </div>
      <label class="online-toggle">
        <input
          type="checkbox"
          checked={online}
          onChange={(e) => {
            api().setSetting('online', (e.target as HTMLInputElement).checked);
            api().notify('Online play changes apply after reloading the page.');
          }}
        />
        Online play (reload to apply)
      </label>
      <p class="muted fine-print">Online play connects you directly to other players (peer-to-peer). Other players can see your IP address, as with any peer-to-peer game. {netUi.warning.value ?? ''}</p>
    </div>
  );
}

export function SocialPanel() {
  const [tab, setTab] = useState<SocialTab>(netUi.party.value.length ? 'party' : 'nearby');
  const tabs: { id: SocialTab; label: string }[] = [
    { id: 'party', label: `Party${netUi.party.value.length ? ` (${netUi.party.value.length}/${PARTY_MAX})` : ''}` },
    { id: 'friends', label: `Friends (${netUi.friends.value.filter((f) => f.online).length}/${netUi.friends.value.length})` },
    { id: 'nearby', label: `Nearby (${netUi.zonePeers.value.length})` },
    { id: 'guild', label: 'Guild' },
    { id: 'online', label: 'Connection' },
  ];
  return (
    <Window title="Social" width={640} onClose={closePanel}>
      <div class="tabs">
        {tabs.map((t) => (
          <button key={t.id} class="tab" aria-selected={tab === t.id} onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>
      <div class="social-body">
        {tab === 'party' ? <PartyTab /> : tab === 'friends' ? <FriendsTab /> : tab === 'nearby' ? <NearbyTab /> : tab === 'guild' ? <GuildTab /> : <OnlineTab />}
      </div>
    </Window>
  );
}

// ===================================================================== trade

function OfferList({ items, silver }: { items: TradeLine[]; silver: number }) {
  return (
    <ul class="offer-list">
      {items.map((l) => {
        const d = ITEMS[l.id] ? itemDef(l.id) : null;
        return (
          <li key={l.id} style={{ color: d ? `var(--r-${d.rarity})` : undefined }}>
            {d?.name ?? l.id} <span class="muted">×{l.count}</span>
          </li>
        );
      })}
      {silver > 0 ? <li class="silver">{silver.toLocaleString()} silver</li> : null}
      {items.length === 0 && silver === 0 ? <li class="muted">Nothing offered</li> : null}
    </ul>
  );
}

export function TradeWindow() {
  const t = netUi.trade.value;
  const [pick, setPick] = useState('');
  const [count, setCount] = useState(1);
  const [silver, setSilver] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  void ui.version.value;
  const s = net();
  const prog = api().progression;
  if (!t || !s || !prog) return null;
  const tradable = Object.entries(prog.char.inventory.stacks).filter(([id, n]) => n > 0 && s.trade.session && ITEMS[id] && ['material', 'consumable', 'battle', 'book', 'gem', 'food', 'junk'].includes(itemDef(id).kind));
  const setOffer = (items: TradeLine[], sv: number) => setErr(s.trade.setOffer({ items, silver: sv }));
  const addLine = () => {
    if (!pick) return;
    const have = prog.char.inventory.stacks[pick] ?? 0;
    const n = Math.max(1, Math.min(have, Math.floor(count)));
    const items = [...t.mine.items.filter((l) => l.id !== pick), { id: pick, count: n }];
    setOffer(items, t.mine.silver);
  };
  return (
    <div class="trade-window panel interactive" onPointerDown={(e) => e.stopPropagation()}>
      <div class="panel__title">
        Trade with {t.peerName}
        <button class="panel__close" onClick={() => s.trade.cancel()}>
          ✕
        </button>
      </div>
      <div class="trade-cols">
        <div class={`trade-col${t.mine.locked ? ' locked' : ''}`}>
          <h4>Your offer {t.mine.locked ? '🔒' : ''}</h4>
          <OfferList items={t.mine.items} silver={t.mine.silver} />
          {!t.mine.locked ? (
            <div class="trade-edit">
              <select value={pick} onChange={(e) => setPick((e.target as HTMLSelectElement).value)}>
                <option value="">Choose an item…</option>
                {tradable.map(([id, n]) => (
                  <option key={id} value={id}>
                    {itemDef(id).name} ({n})
                  </option>
                ))}
              </select>
              <input type="number" min={1} value={count} onInput={(e) => setCount(Number((e.target as HTMLInputElement).value))} onKeyDown={(e) => e.stopPropagation()} />
              <button class="btn" onClick={addLine}>
                Add
              </button>
              <button class="btn" onClick={() => setOffer([], t.mine.silver)}>
                Clear
              </button>
              <label>
                Silver
                <input type="number" min={0} value={silver} onInput={(e) => setSilver(Number((e.target as HTMLInputElement).value))} onKeyDown={(e) => e.stopPropagation()} onBlur={() => setOffer(t.mine.items, Math.max(0, Math.floor(silver)))} />
              </label>
            </div>
          ) : null}
        </div>
        <div class={`trade-col${t.theirs.locked ? ' locked' : ''}`}>
          <h4>
            {t.peerName}'s offer {t.theirs.locked ? '🔒' : ''}
          </h4>
          <OfferList items={t.theirs.items} silver={t.theirs.silver} />
          {t.theirs.confirmed ? <p class="ok">{t.peerName} confirmed.</p> : null}
        </div>
      </div>
      {err ? <p class="err">{err}</p> : null}
      <div class="ni-btns">
        <button class="btn" onClick={() => s.trade.lock(!t.mine.locked)}>
          {t.mine.locked ? 'Unlock' : 'Lock offer'}
        </button>
        <button class="btn btn--gold" disabled={!t.mine.locked || !t.theirs.locked || t.mine.confirmed} onClick={() => s.trade.confirm()}>
          {t.mine.confirmed ? 'Confirmed' : 'Confirm trade'}
        </button>
        <button class="btn" onClick={() => s.trade.cancel()}>
          Cancel
        </button>
      </div>
    </div>
  );
}
